import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import { store } from '../../../../redux/store';
import { http_get as hubGet, http_post as hubPost } from '../../utils/hub_client';
import { http_post as apiPost } from '../../utils/api_client';
import { HUB_ENDPOINTS, API_ENDPOINTS } from '../../utils/endpoints';
import BlockRenderer from '../../docs/blocks/BlockRenderer';
import { brandingStyle, loadGoogleFont } from '../../docs/branding';
import '../../docs/DocReader.scss';

// BL-119 — learner-facing Doc reader, mounted by hub/show for a published doc
// (material_type 'docs'). Reuses the doc branding layout + BlockRenderer, but
// talks to the hub (aud_token) and tracks progress: each non-group section is a
// "step" that flips to completed once scrolled to the end (same StepProgress
// substrate paths use). Anonymous learners get a magic sign-in prompt so their
// progress can be saved to a real identity; a doc can also require sign-in.
const HEADING_TYPES = ['h1', 'h2', 'h3'];

// Flatten sections into ordered nav entries; non-group entries get a 1-based
// `position` matching the backend's section-step numbering.
const flattenNav = (sections) => {
  const out = [];
  let pos = 0;
  const push = (s, depth) => {
    const isGroup = !!s.is_group;
    if (!isGroup) pos += 1;
    out.push({ id: s.id, title: s.title, is_group: isGroup, depth, blocks: s.blocks || [], position: isGroup ? null : pos });
  };
  (sections || []).forEach((s) => {
    push(s, 0);
    (s.children || []).forEach((c) => push(c, 1));
  });
  return out;
};

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const BookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Slim inline magic-link form. Reuses the existing /magic_link/generate endpoint.
const SignInForm = ({ companySlug, compact, onDone }) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setErr(false);
    try {
      await apiPost(API_ENDPOINTS.SEND_MAGIC_LINK, {
        email: email.trim(),
        company_name: companySlug,
        return_to: window.location.pathname
      });
      setSent(true);
      if (onDone) onDone();
    } catch (e2) {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };

  if (sent) return <div className="doc-r-signin__sent">Check your email for a sign-in link.</div>;

  return (
    <form className={`doc-r-signin__form ${compact ? 'is-compact' : ''}`} onSubmit={submit}>
      <input type="email" required value={email} placeholder="you@company.com" onChange={(e) => setEmail(e.target.value)} />
      <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send link'}</button>
      {err && <div className="doc-r-signin__err">Couldn’t send. Try again.</div>}
    </form>
  );
};

const ShowPage = ({ id }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState('');
  const [progress, setProgress] = useState({});
  const [showSignIn, setShowSignIn] = useState(false);
  const [helpful, setHelpful] = useState(null);
  const endRef = useRef(null);
  const companySlug = window.location.pathname.split('/')[1];

  useEffect(() => {
    let cancelled = false;
    hubGet(HUB_ENDPOINTS.GET_DOC(id))
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.error) { setError(true); return; }
        setDoc(data);
        setProgress(data.progress || {});
        const nav = flattenNav(data.content?.sections);
        const first = nav.find((n) => !n.is_group);
        setActiveId(first ? first.id : null);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => { loadGoogleFont(doc?.branding?.font); }, [doc]);
  useEffect(() => { setHelpful(null); }, [activeId]);

  const nav = useMemo(() => flattenNav(doc?.content?.sections), [doc]);
  const active = nav.find((n) => n.id === activeId);

  const visibleNav = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return nav;
    return nav.filter((n) => (n.title || '').toLowerCase().includes(q));
  }, [nav, query]);

  const headings = (active?.blocks || []).filter((b) => HEADING_TYPES.includes(b.type) && (b.text || '').trim());

  const post = (payload) => hubPost(HUB_ENDPOINTS.DOC_SECTION_PROGRESS(id), payload)
    .then(({ data }) => { if (data?.progress) setProgress(data.progress); })
    .catch(() => {});

  // Mark the active section reached (in_progress) when it opens.
  useEffect(() => {
    if (!active || active.is_group || doc?.locked) return;
    if (progress[active.id] === 'completed') return;
    post({ section_uid: active.id, section_position: active.position, status: 'in_progress' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, doc]);

  // Complete the active section once its end scrolls into view.
  useEffect(() => {
    if (!active || active.is_group || doc?.locked) return undefined;
    const el = endRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting) && progress[active.id] !== 'completed') {
        setProgress((p) => ({ ...p, [active.id]: 'completed' }));
        post({ section_uid: active.id, section_position: active.position, status: 'completed' });
      }
    }, { root: null, threshold: 0.9 });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, doc, active && active.blocks && active.blocks.length]);

  const scrollToBlock = (blockId) => {
    const el = document.getElementById(`blk-${blockId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) return <div className="doc-r-load">Loading…</div>;
  if (error || !doc) return <div className="doc-r-load">This doc isn’t available.</div>;

  const brand = doc.branding || {};
  const showSearch = brand.show_search !== false;
  const brandName = brand.brand_name || doc.name || 'Docs';
  const shellStyle = brandingStyle(brand);
  const signedIn = doc.learner?.signed_in;
  const sectionSteps = nav.filter((n) => !n.is_group);
  const completedCount = sectionSteps.filter((n) => progress[n.id] === 'completed').length;

  // Sign-in gate: a required doc, viewed anonymously, withholds content.
  if (doc.locked) {
    return (
      <div className="doc-r-shell" style={shellStyle}>
        <div className="doc-r-gate">
          <div className="doc-r-gate__card">
            <div className="doc-r-gate__logo">
              {brand.logo_url ? <img src={brand.logo_url} alt={brandName} /> : <BookIcon />}
            </div>
            <h1 className="doc-r-gate__t">Sign in to read {doc.name}</h1>
            <p className="doc-r-gate__d">Enter your email and we’ll send you a secure sign-in link.</p>
            <SignInForm companySlug={companySlug} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <div className="doc-r-shell" style={shellStyle}>
        <div className="doc-r">
          <aside className="doc-r-side">
            <div className={`doc-r-logo ${brand.logo_url ? 'doc-r-logo--img' : ''}`}>
              {brand.logo_url ? (
                <img className="doc-r-logo__img" src={brand.logo_url} alt={brandName} />
              ) : (
                <>
                  <span className="doc-r-logo__ic"><BookIcon /></span>
                  <span className="doc-r-logo__t">{brandName}</span>
                </>
              )}
            </div>
            <nav className="doc-r-nav">
              {visibleNav.map((n) => (n.is_group ? (
                <div key={n.id} className={`doc-r-group depth-${n.depth}`}>{n.title}</div>
              ) : (
                <div key={n.id} className={`doc-r-link depth-${n.depth} ${activeId === n.id ? 'on' : ''}`} onClick={() => setActiveId(n.id)}>
                  {progress[n.id] === 'completed' && <span className="doc-r-link__check"><CheckIcon /></span>}
                  <span className="doc-r-link__t">{n.title}</span>
                </div>
              )))}
              {visibleNav.length === 0 && <div className="doc-r-navempty">{query.trim() ? 'No matches.' : 'No sections yet.'}</div>}
            </nav>
          </aside>

          <div className="doc-r-body">
            <header className="doc-r-top">
              {showSearch && (
                <div className="doc-r-search">
                  <span className="doc-r-search__ic"><SearchIcon /></span>
                  <input value={query} placeholder="Search documentation…" onChange={(e) => setQuery(e.target.value)} />
                </div>
              )}
              <div className="doc-r-top__sp" />
              {signedIn ? (
                <div className="doc-r-who">{doc.learner?.first_name ? `Hi, ${doc.learner.first_name}` : 'Signed in'}</div>
              ) : (
                <div className="doc-r-signin">
                  {showSignIn ? (
                    <SignInForm companySlug={companySlug} compact onDone={() => {}} />
                  ) : (
                    <button type="button" className="doc-r-signin__btn" onClick={() => setShowSignIn(true)}>Sign in to save progress</button>
                  )}
                </div>
              )}
            </header>

            <div className="doc-r-cols">
              <main className="doc-r-main">
                {!active ? (
                  <div className="doc-r-empty">This doc has no content yet.</div>
                ) : (
                  <div className="doc-r-content">
                    <h1 className="doc-r-page-title">{active.title}</h1>
                    {(active.blocks || []).map((b) => <BlockRenderer key={b.id} block={b} companySlug={companySlug} />)}
                    {(active.blocks || []).length === 0 && <div className="doc-r-empty">Nothing on this page yet.</div>}
                    <div ref={endRef} className="doc-r-end" aria-hidden />
                  </div>
                )}
              </main>

              <aside className="doc-r-toc">
                {sectionSteps.length > 0 && (
                  <div className="doc-r-toc__sec">
                    <div className="doc-r-toc__h">Your progress</div>
                    <div className="doc-r-prog">
                      <div className="doc-r-prog__bar"><span style={{ width: `${Math.round((completedCount / sectionSteps.length) * 100)}%` }} /></div>
                      <div className="doc-r-prog__c">{completedCount} of {sectionSteps.length} sections</div>
                    </div>
                  </div>
                )}
                {headings.length > 0 && (
                  <div className="doc-r-toc__sec">
                    <div className="doc-r-toc__h">On this page</div>
                    {headings.map((h) => (
                      <div key={h.id} className={`doc-r-toc__link is-${h.type}`} onClick={() => scrollToBlock(h.id)}>{h.text}</div>
                    ))}
                  </div>
                )}
                <div className="doc-r-toc__sec">
                  <div className="doc-r-toc__h">Was this helpful?</div>
                  {helpful ? (
                    <div className="doc-r-help__thanks">Thanks for the feedback.</div>
                  ) : (
                    <div className="doc-r-help__row">
                      <button type="button" className="doc-r-help__b" onClick={() => setHelpful('yes')}>Yes</button>
                      <button type="button" className="doc-r-help__b" onClick={() => setHelpful('no')}>No</button>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </Provider>
  );
};

export default ShowPage;
